import { makeStyles } from '@mui/styles';
import Tab from 'apollo-react/components/Tab';
import Tabs from 'apollo-react/components/Tabs';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import HeaderSection from './HeaderSection';
import {
  getOpenDetailFormTabEntityDisplay,
  getOpenErrorsTabEntityDisplay,
  getOpenStepFormTabEntityDisplay,
} from './selectors';
import TaskStatus from './TaskStatus';
import { getTaskStatusDisabled } from './TaskStatus/selectors';
import {
  setOpenDetailFormTabEntityDisplay,
  setOpenErrorsTabEntityDisplay,
  setOpenStepFormTabEntityDisplay,
} from '../../actions/EntityDisplayActions';
import ErrorDisplay from '../../components/ErrorDisplay';
import { ERRORS } from '../../constants/Common';
import { useFormSelector, usePrevious } from '../../custom-hooks';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { updateSelectedSubGrids } from '../../form-designer/actions/runtime.actions';
import DetailForm from '../../form-designer/components/DetailForm';
import FormFooter from '../../form-designer/components/FormFooter';
import StepForm from '../../form-designer/components/StepForm';
import { FORM_KEYS } from '../../form-designer/constants/form-manager.constants';
import { FormContext } from '../../form-designer/context';
import { isFormLoading } from '../../form-designer/selectors/common.selectors';
import { getSelectedSubGridsCount } from '../../form-designer/selectors/runtime.selectors';
import { equalityFn } from '../../form-designer/utils/form-manager.utils';
import { getPreventFormMounting } from '../../selectors/AppConfigsSelectors';
import { getErrorDisplayMessages } from '../../selectors/MessagesSelectors';
import {
  getDecisionTreeIframeSrc,
  getInspectionMeasurementDataIframeSrc,
} from '../../selectors/WorkflowSelectors';

const useStyles = makeStyles({
  containerFlex: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  root: {
    backgroundColor: '#fff',
    padding: ({ isStepFormLoading }) => `0 8px ${isStepFormLoading ? 0 : 63}px`,
    flex: 1,
  },
  tabs: {
    marginBottom: 5,
  },
  containerIframe: {
    flex: 1,
  },
  containerDisplayNone: {
    display: 'none',
  },
  iframe: {
    border: 0,
  },
});

const MobileMainSection = ({ isDetailOnly }) => {
  const dispatch = useDispatch();
  const translateString = useTranslation();
  const isStepFormLoading = useSelector(isFormLoading(FORM_KEYS.STEP));
  const isDetailFormLoading = useSelector(isFormLoading(FORM_KEYS.DETAIL));
  const classes = useStyles({ isStepFormLoading });
  const taskStatusDisabled = useSelector(getTaskStatusDisabled);
  const errors = useSelector(getErrorDisplayMessages, equalityFn);
  const openStepFormTab = useSelector(getOpenStepFormTabEntityDisplay);
  const openDetailFormTab = useSelector(getOpenDetailFormTabEntityDisplay);
  const openErrorsTab = useSelector(getOpenErrorsTabEntityDisplay);
  const selectedSubGridsCount = useFormSelector(getSelectedSubGridsCount, {
    formKey: FORM_KEYS.STEP,
  });
  const previousErrorsCount = usePrevious(errors.length);
  const preventFormMounting = useSelector(getPreventFormMounting);
  const decisionTreeIframeSrc = useSelector(getDecisionTreeIframeSrc);
  const inspectionMeasurementDataIframeSrc = useSelector(getInspectionMeasurementDataIframeSrc);

  const [currentTab, setCurrentTab] = useState(isDetailOnly ? 1 : 0);

  const isIframeSrcDefined = decisionTreeIframeSrc || inspectionMeasurementDataIframeSrc;

  useEffect(() => {
    if (openStepFormTab) {
      setCurrentTab(0);
      dispatch(setOpenStepFormTabEntityDisplay(false));
    }
  }, [dispatch, openStepFormTab]);

  useEffect(() => {
    if (openDetailFormTab) {
      setCurrentTab(1);
      dispatch(setOpenDetailFormTabEntityDisplay(false));
    }
  }, [dispatch, openDetailFormTab]);

  useEffect(() => {
    if (openErrorsTab || preventFormMounting) {
      setCurrentTab(2);
      dispatch(setOpenErrorsTabEntityDisplay(false));
    }
  }, [dispatch, openErrorsTab, preventFormMounting]);

  useEffect(() => {
    if (previousErrorsCount !== errors.length && errors.length > 0) {
      setCurrentTab(2);
    }
  }, [dispatch, errors, previousErrorsCount]);

  useEffect(() => {
    if (currentTab === 2 && selectedSubGridsCount) {
      dispatch(updateSelectedSubGrids([], FORM_KEYS.STEP));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);

  const handleTabChange = (_, tabValue) => {
    setCurrentTab(tabValue);
  };

  const tabContent = [
    <React.Fragment key="StepForm" />, // placeholder
    <DetailForm key="DetailForm" isLoading={isDetailFormLoading} />,
    <ErrorDisplay key="ErrorDisplay" />,
    <TaskStatus key="TaskStatus" />,
  ];

  return (
    <div className={classes.containerFlex}>
      <FormContext.Provider value={FORM_KEYS.DETAIL}>
        <HeaderSection />
      </FormContext.Provider>

      <div className={isIframeSrcDefined ? classes.containerDisplayNone : classes.root}>
        <Tabs className={classes.tabs} value={currentTab} onChange={handleTabChange}>
          <Tab
            id="mobileStepFormTab"
            label={translateString('Step')}
            disabled={isStepFormLoading}
          />
          <Tab
            id="mobileDetailFormTab"
            label={translateString('Detail')}
            disabled={isDetailFormLoading}
          />
          <Tab
            label={`${translateString(ERRORS)} (${errors.length})`}
            disabled={errors.length < 1}
          />
          <Tab label={translateString('Task Status')} disabled={taskStatusDisabled} />
        </Tabs>

        {/* we want to always keep Step Form in DOM (for validation purpose) */}
        <div style={{ display: currentTab === 0 ? 'block' : 'none' }}>
          <StepForm key="StepForm" isLoading={isStepFormLoading} />
        </div>

        {tabContent[currentTab]}
      </div>

      {isIframeSrcDefined && (
        <div className={classes.containerIframe}>
          <iframe
            title={
              decisionTreeIframeSrc ? 'DecisionTreeIframeSrc' : 'InspectionMeasurementDataIframeSrc'
            }
            src={decisionTreeIframeSrc ?? inspectionMeasurementDataIframeSrc}
            id="frameDecision"
            width="100%"
            height="100%"
            className={classes.iframe}
          />
        </div>
      )}

      <FormContext.Provider value={FORM_KEYS.STEP}>
        <div className={isIframeSrcDefined ? classes.containerDisplayNone : ''}>
          {!isStepFormLoading && <FormFooter />}
        </div>
      </FormContext.Provider>
    </div>
  );
};

export default MobileMainSection;
